import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PALETTES, PALETTE_KEYS, DEFAULT_PALETTE, applyPalette, paletteHsl } from '../lib/palettes';

const PALETTE_KEY = 'routine-app-palette';
const THEME_KEY = 'routine-app-theme';
const AUTO_THEME_KEY = 'routine-app-theme-auto';

const THEME_MODES = ['light', 'dark', 'black'];

// Fixed daytime window: 06:00 ≤ hour < 18:00 is "day" → light.
// Sunrise/sunset would be ideal but would force a geolocation prompt
// or a sun-position library for a marginal payoff. The fixed window is
// cheap, predictable, and good enough as a v1.
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 18;

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function isDaytimeNow() {
  const hour = new Date().getHours();
  return hour >= DAY_START_HOUR && hour < DAY_END_HOUR;
}

function readInitialMode() {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved && THEME_MODES.includes(saved)) return saved;
  // No saved choice → ship dark as the canonical default. The previous
  // behaviour (respect prefers-color-scheme) is now superseded by the
  // ground-truth defaults the owner pinned for everyone.
  return 'dark';
}

function readInitialAuto() {
  return localStorage.getItem(AUTO_THEME_KEY) === '1';
}

// Resolve which theme should actually paint right now, given the
// user's saved mode and whether auto-by-time is on. When auto is on:
//   - Daytime → always 'light' (matches the user's request: light in
//     morning, dark in evening).
//   - Night → honour the saved dark variant (Dark vs Black/OLED) so a
//     user who prefers OLED at night still gets it.
function resolveEffective(mode, auto) {
  if (!auto) return mode;
  if (isDaytimeNow()) return 'light';
  return mode === 'black' ? 'black' : 'dark';
}

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(readInitialMode);
  const [autoTheme, setAutoTheme] = useState(readInitialAuto);
  // `effectiveMode` is what we actually apply to <html>. It's derived
  // from (themeMode, autoTheme, time-of-day) and re-evaluated by the
  // tick effect below so the page flips at the 06:00 / 18:00 boundary
  // without a reload.
  const [effectiveMode, setEffectiveMode] = useState(() =>
    resolveEffective(readInitialMode(), readInitialAuto()),
  );

  const [palette, setPalette] = useState(() => {
    const saved = localStorage.getItem(PALETTE_KEY);
    return saved && PALETTES[saved] ? saved : DEFAULT_PALETTE;
  });

  // Persist mode + auto-flag whenever they change, then recompute the
  // effective mode immediately so user actions land without waiting
  // for the next tick.
  useEffect(() => {
    localStorage.setItem(THEME_KEY, themeMode);
    setEffectiveMode(resolveEffective(themeMode, autoTheme));
  }, [themeMode, autoTheme]);

  useEffect(() => {
    localStorage.setItem(AUTO_THEME_KEY, autoTheme ? '1' : '0');
  }, [autoTheme]);

  // Periodic re-evaluation while auto-by-time is on. 60s cadence is
  // enough to catch the daytime boundary without busy-looping. Also
  // re-check on visibilitychange so an app that was backgrounded
  // overnight flips immediately when the user returns.
  useEffect(() => {
    if (!autoTheme) return undefined;
    const tick = () => setEffectiveMode(resolveEffective(themeMode, true));
    tick();
    const interval = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [autoTheme, themeMode]);

  // Apply theme class(es) to <html>. dark + black both flip `dark`;
  // black additionally toggles `oled` so we can override --background
  // and --card to pure 0 0% 0% in index.css.
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveMode === 'dark' || effectiveMode === 'black') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    if (effectiveMode === 'black') {
      root.classList.add('oled');
    } else {
      root.classList.remove('oled');
    }
  }, [effectiveMode]);

  // Write the chosen palette's HSL shades into :root on mount and every
  // time the user picks a different palette. Components consuming
  // bg-app-primary-* / text-app-primary-* utility classes pick the new
  // colour up automatically since the classes resolve to var(--app-primary-NNN).
  useEffect(() => {
    applyPalette(palette);
    localStorage.setItem(PALETTE_KEY, palette);
  }, [palette]);

  // Cycle light → dark → light. 'black' isn't reachable from the toggle
  // (Settings UI sets it explicitly) since most consumers only care
  // about light vs dark. Auto stays off when the user manually toggles
  // — they took explicit control.
  const toggleTheme = () => {
    if (autoTheme) setAutoTheme(false);
    setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  // Returns the resolved hsl(...) string for the current palette + shade.
  // Use in places that don't flow through Tailwind classes — recharts
  // stroke/fill attributes, SVG gradients, inline styles for colours
  // that can't be expressed as utility classes.
  const primaryHsl = useCallback(
    (shade = 500) => paletteHsl(palette, shade),
    [palette]
  );

  // `isDark` reflects what's currently painted, not what the user
  // chose — consumers reading this expect "is the screen dark right
  // now" semantics (e.g. swapping a logo, picking a chart colour).
  const isDark = effectiveMode === 'dark' || effectiveMode === 'black';

  const value = {
    isDark,
    themeMode,
    setThemeMode,
    themeModes: THEME_MODES,
    toggleTheme,
    autoTheme,
    setAutoTheme,
    effectiveMode,
    palette,
    setPalette,
    paletteKeys: PALETTE_KEYS,
    palettes: PALETTES,
    primaryHsl,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
